import { Link } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';

const LegalAgreement = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src="/apple-touch-icon.png" alt="EightyThreeLabs" className="w-8 h-8" />
              <span className="text-xl font-semibold">
                <span className="text-brand-primary">EightyThree</span>
                <span className="text-black dark:text-white">Labs</span>
              </span>
            </Link>
            
            {/* Dark Mode Toggle */}
            <DarkModeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 shadow-xl">
          <Link 
            to="/signup" 
            className="inline-flex items-center text-brand-primary hover:text-green-700 dark:hover:text-green-400 mb-8 transition-colors"
          >
            ← Back to Signup
          </Link>
          
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 text-center">User Agreement</h1>
          <div className="text-center text-gray-600 dark:text-gray-400 mb-8 italic">
            Terms of Service, Privacy Policy & Cookie Policy
          </div>
          <div className="text-center text-gray-600 dark:text-gray-400 mb-8 italic">
            Effective Date: January 1, 2025
          </div>
          
          <div className="prose max-w-none space-y-8">
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-6 rounded-r-lg mb-8">
              <h2 className="text-xl font-bold text-blue-900 dark:text-blue-300 mb-3">Important Legal Notice</h2>
              <p className="text-blue-800 dark:text-blue-300">
                By creating an account with EightyThreeLabs, you are entering into a legally binding agreement. Please read this entire document carefully as it contains important information about your rights and responsibilities, including limitations of liability, binding arbitration clauses, and class action waivers.
              </p>
            </div>

            {/* Terms of Service Section */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 border-b-2 border-brand-primary pb-3">Terms of Service</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance and Binding Agreement</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                By accessing, using, or creating an account with EightyThreeLabs ("Service," "Platform," "we," "us," or "our"), you ("User," "you," or "your") agree to be legally bound by these Terms of Service and all applicable laws. If you disagree with any part of these terms, you must not use our Service.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Service Description and Modifications</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                EightyThreeLabs provides online educational brain training games, mathematical puzzles, and related services. We reserve the absolute right to modify, suspend, or discontinue any aspect of our Service at any time without notice or liability to users.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. User Accounts and Responsibilities</h3>
              <div className="text-gray-700 dark:text-gray-300 mb-4">
                <p className="mb-3">You are fully responsible for:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Maintaining the security and confidentiality of your account credentials</li>
                  <li>All activities occurring under your account, whether authorized or not</li>
                  <li>Providing accurate, current, and complete information</li>
                  <li>Compliance with all applicable laws and regulations</li>
                  <li>Any content you submit or actions you take through the Service</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Prohibited Uses and Conduct</h3>
              <div className="text-gray-700 dark:text-gray-300 mb-4">
                <p className="mb-3">You expressly agree NOT to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Use the Service for any unlawful purposes or in violation of any regulations</li>
                  <li>Attempt to gain unauthorized access to our systems, other accounts, or networks</li>
                  <li>Use automated tools, bots, scripts, or other means to manipulate the Service</li>
                  <li>Interfere with, disrupt, or damage the Service or our servers</li>
                  <li>Transmit viruses, malware, or other harmful code</li>
                  <li>Reverse engineer, decompile, or attempt to extract source code</li>
                  <li>Violate intellectual property rights</li>
                  <li>Harass, threaten, or harm other users</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Intellectual Property Rights</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                All content, features, functionality, games, designs, text, graphics, and other materials on our Service are owned exclusively by EightyThreeLabs and are protected by copyright, trademark, patent, and other intellectual property laws. Unauthorized use is strictly prohibited and may result in legal action.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Payment Terms and Subscriptions</h3>
              <div className="text-gray-700 dark:text-gray-300 mb-4">
                <ul className="list-disc list-inside space-y-1">
                  <li>All subscription fees are charged in advance and are non-refundable except as required by law</li>
                  <li>We may change pricing with 30 days advance notice</li>
                  <li>You authorize us to charge your payment method for all applicable fees</li>
                  <li>Failure to pay may result in immediate suspension or termination of your account</li>
                  <li>You are responsible for all taxes associated with your subscription</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">7. LIMITATION OF LIABILITY AND DISCLAIMERS</h3>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-4">
                <p className="text-red-800 dark:text-red-300 font-semibold mb-2">IMPORTANT: PLEASE READ CAREFULLY</p>
                <div className="text-red-700 dark:text-red-300 space-y-2">
                  <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND</li>
                    <li>WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
                    <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                    <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID IN THE 12 MONTHS PRECEDING THE CLAIM</li>
                    <li>WE ARE NOT RESPONSIBLE FOR ANY LOSS OF DATA, PROFITS, OR BUSINESS INTERRUPTION</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">8. Indemnification</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You agree to defend, indemnify, and hold harmless EightyThreeLabs, its officers, directors, employees, and affiliates from any claims, damages, losses, liabilities, costs, or expenses (including reasonable attorneys' fees) arising from your use of the Service, violation of these Terms, or infringement of any rights of another party.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">9. BINDING ARBITRATION AND CLASS ACTION WAIVER</h3>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 dark:text-yellow-300 font-semibold mb-2">BINDING ARBITRATION CLAUSE</p>
                <div className="text-yellow-700 dark:text-yellow-300 space-y-2">
                  <p>Any dispute arising from these Terms shall be resolved through binding arbitration rather than in court, except you may assert claims in small claims court.</p>
                  <p><strong>CLASS ACTION WAIVER:</strong> You waive any right to participate in a class-action lawsuit or class-wide arbitration against us.</p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">10. Termination</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will cease immediately, and we may delete your account and data.
              </p>
            </section>

            {/* Privacy Policy Section */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 border-b-2 border-brand-primary pb-3">Privacy Policy</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Collection and Use</h3>
              <div className="text-gray-700 dark:text-gray-300 mb-4">
                <p className="mb-3">We collect and process the following information:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Account Information:</strong> Email, password, profile data</li>
                  <li><strong>Usage Data:</strong> Game scores, progress, session data, behavioral analytics</li>
                  <li><strong>Device Information:</strong> IP address, browser type, device identifiers</li>
                  <li><strong>Payment Information:</strong> Billing details processed by third-party providers</li>
                  <li><strong>Communications:</strong> Support messages, feedback, survey responses</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Information Sharing</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may share your information with service providers, for legal compliance, in business transfers, or with your consent. We do not sell personal information to third parties for marketing purposes.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Data Retention and Security</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We retain your data as long as your account is active or as needed to provide services. We implement security measures to protect your information, but cannot guarantee absolute security.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Your Rights</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Subject to applicable laws, you may request access, correction, or deletion of your personal information. Some rights may be limited by legal requirements or legitimate business needs.
              </p>
            </section>

            {/* Cookie Policy Section */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 border-b-2 border-brand-primary pb-3">Cookie Policy</h2>
              
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use cookies and similar technologies for functionality, analytics, and advertising. By using our Service, you consent to our use of cookies as described in our full <Link to="/cookie-policy" className="text-brand-primary hover:text-green-700 dark:hover:text-green-400 transition-colors">Cookie Policy</Link>.
              </p>
            </section>

            {/* Final Legal Notices */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 border-b-2 border-brand-primary pb-3">Additional Legal Provisions</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Governing Law</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                These Terms are governed by and construed in accordance with applicable laws, without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Severability</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Changes to Terms</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                For questions about these Terms, please contact us through our website at <Link to="/" className="text-brand-primary hover:text-green-700 dark:hover:text-green-400 transition-colors">eightythreelabs.com</Link>.
              </p>
            </section>

            <div className="bg-gray-100 dark:bg-gray-800 border rounded-lg p-6 mt-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Summary of Key Points</h3>
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <p>• By using our service, you agree to binding arbitration for disputes</p>
                <p>• You waive rights to class-action lawsuits</p>
                <p>• Our liability is limited to the amount you've paid us</p>
                <p>• We may terminate your account at any time</p>
                <p>• You're responsible for all activity on your account</p>
                <p>• We collect and use your data as described in our Privacy Policy</p>
              </div>
            </div>
          </div>

          <hr className="my-8 border-gray-200 dark:border-gray-700" />
          
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
            © 2025 EightyThreeLabs. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalAgreement;